'use client'

import { useInputWrapper } from '../../../../hooks'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import React from 'react'
import { isOptionSelected, toArray } from '../_helpers/selectHelpers'
import { useComboboxListbox } from '../_hooks/useComboboxListbox'
import { useNativeSelectChangeReset } from '../_hooks/useNativeSelectChangeReset'
import { useSyncDefaultValue } from '../_hooks/useSyncDefaultValue'
import { SelectTriggerButton } from './SelectTriggerButton'
import { SelectListbox } from './SelectListbox'
import { SelectOption } from './SelectOption'
import { HTMLSelectInputProps } from '../HTMLSelectInput'

/**
 * Multi-select combobox for HTMLSelectInput. Keeps a hidden native select element with multiple attribute in sync for form
 * submission and reset. Renders a custom listbox with type-ahead, keyboard navigation, and optional clear.
 */
export const MultiSelectCombobox = ({
    className,
    options: optionsProp,
    disabled = false,
    clearable = false,
    defaultValue,
    error,
    name,
    id,
    'data-testid': dataTestId,
    required = false,
}: HTMLSelectInputProps): ReactElement => {
    const { classes } = useInputWrapper(false, disabled, error)
    const filteredOptions = optionsProp.filter((option) => !!option)

    const [selected, setSelected] = useState<(string | number)[]>(() => toArray(defaultValue))

    const onActivateOption = useCallback(
        (value: string | number) => {
            const option = filteredOptions.find((filteredOption) => filteredOption.value == value)
            if (option?.disabled) return

            setSelected((previous) =>
                previous.some((selectedValue) => selectedValue == value)
                    ? previous.filter((entry) => entry != value)
                    : [...previous, value],
            )
        },
        [filteredOptions],
    )

    const {
        selectContainerRef,
        selectInputRef,
        listBoxTriggerRef,
        listboxId,
        isOpen,
        focusedIndex,
        openListbox,
        closeListbox,
        handleComboboxKeyDown,
        baseClasses,
        focusedOptionId,
    } = useComboboxListbox({
        disabled,
        className,
        classes: classes ?? '',
        filteredOptions,
        onActivateOption,
    })

    useSyncDefaultValue(defaultValue, setSelected, toArray)

    /**
     * Tracks the last selection we already surfaced with a bubbling `change` on the hidden `<select>`.
     * Do not remove: dispatching `change` runs `handleChange`, which reads values in **DOM option order**,
     * while React `selected` may use a different order for the same set. Without this guard we emit duplicate
     * `change` events or extra renders. The sorted serialized key compares the selection as a set.
     */
    const previousSelectedSerializedReference = useRef<string>('')

    useEffect(() => {
        const selectElement = selectInputRef.current
        if (!selectElement) return
        const selectElementOptions = Array.from(selectElement.options)
        for (let index = 0; index < selectElementOptions.length; index += 1) {
            selectElementOptions[index].selected = isOptionSelected(selected, selectElementOptions[index].value)
        }
        const serialized = [...selected].map(String).sort().join('\u0000')
        if (serialized === previousSelectedSerializedReference.current) return
        previousSelectedSerializedReference.current = serialized
        selectElement.dispatchEvent(new Event('change', { bubbles: true }))
    }, [selected, filteredOptions?.length])

    const handleChange = useCallback(() => {
        const selectElement = selectInputRef.current
        if (!selectElement) return
        const nextSelected = Array.from(selectElement.selectedOptions).map((option) => option.value)
        setSelected((previous) => {
            if (
                previous.length === nextSelected.length &&
                previous.every((value, index) => value == nextSelected[index])
            ) {
                return previous
            }
            return nextSelected
        })
    }, [])

    const handleReset = useCallback(() => {
        const values = filteredOptions
            .filter((option) => toArray(defaultValue)?.some((defaultOptionValue) => defaultOptionValue == option.value))
            .map((option) => String(option.value))
        setSelected(values)
    }, [filteredOptions, defaultValue])

    useNativeSelectChangeReset(selectInputRef, { onChange: handleChange, onReset: handleReset }, [
        filteredOptions,
        defaultValue,
    ])

    const handleOpenListbox = useCallback(() => {
        const firstSelectedIndex = filteredOptions.findIndex((option) =>
            selected.some((selectedValue) => selectedValue == option.value!),
        )
        openListbox(() => (firstSelectedIndex >= 0 ? firstSelectedIndex : 0))
    }, [filteredOptions, selected, openListbox])

    return (
        <div ref={selectContainerRef} className="relative">
            <select
                ref={selectInputRef}
                multiple
                name={name}
                id={id}
                required={required}
                disabled={disabled}
                style={{ display: 'none' }}
            >
                {filteredOptions.map((option) => (
                    <option
                        key={`${option.value}_${option.label}`}
                        value={option.value!}
                        selected={isOptionSelected(selected, option.value)}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            <SelectTriggerButton
                ref={listBoxTriggerRef}
                baseClasses={baseClasses}
                disabled={disabled}
                isOpen={isOpen}
                onToggle={() => (isOpen ? closeListbox() : handleOpenListbox())}
                onKeyDown={handleComboboxKeyDown}
                id={id}
                dataTestId={dataTestId}
                selected={selected}
                options={filteredOptions}
                listboxId={listboxId}
                ariaActivedescendant={focusedOptionId}
                required={required}
                clearable={clearable}
                onClear={() => {
                    setSelected([])
                    requestAnimationFrame(() => {
                        listBoxTriggerRef.current?.focus()
                    })
                }}
            />
            {isOpen && (
                <SelectListbox id={listboxId} ariaLabelledby={id ? `${id}-label` : undefined} multiselectable>
                    {filteredOptions.map((option, index) => (
                        <SelectOption
                            key={`${option.value}_${option.label}`}
                            option={option}
                            index={index}
                            listboxId={listboxId}
                            isSelected={selected.some((selectedValue) => selectedValue == option.value!)}
                            isFocused={index === focusedIndex}
                            onActivate={onActivateOption}
                        />
                    ))}
                </SelectListbox>
            )}
            <div aria-live="polite" aria-atomic className="sr-only">
                {selected.length === 0 && ''}
                {selected.length === 1 &&
                    `${filteredOptions.find((option) => option.value == selected[0])?.label ?? selected[0]} selected`}
                {selected.length > 1 && `${selected.length} items selected`}
            </div>
        </div>
    )
}
